/**
 * This Vue directive, 'format-number', is used to format the input numbers in a text field.
 * It listens to the 'input' event on the element and formats the input number to a specified number of decimal places.
 * The number of decimal places is passed as a binding value to the directive.
 *
 * The directive also takes into account the locale of the user to format the number accordingly.
 * It uses the 'NumberParser' class to parse the input number based on the locale.
 *
 * The directive also updates the 'v-model' of the element with the formatted number.
 *
 * Usage:
 * In your Vue template, bind the directive to an element like this:
 * <input v-format-number="'2'" v-model="myNumber" />
 * This will format the input number to 2 decimal places.
 *
 * You can replace '2' with any number to round to that many decimal places.
 */
import { defineNuxtPlugin, useRoute } from 'nuxt/app'
import { nextTick } from 'vue'
import type { DirectiveBinding } from 'vue'
import { getLocaleInfo } from '~/lang/locales'
import { NumberParser } from '~/utility/i18n/number-parser'

export default defineNuxtPlugin((nuxtApp) => {
  const route = useRoute()
  nuxtApp.vueApp.directive('format-number', {
    async mounted(el, binding: DirectiveBinding) {
      await nextTick(() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        const input = el.querySelector('input')
        if (!input) {
          return
        }

        const parseNumber = (e: Event) => {
          const target = e.target as HTMLInputElement
          const decimalPlaces = parseInt(binding.value)
          const localeInfo = getLocaleInfo(route.params.lang as string)
          const number = new NumberParser(localeInfo.iso).parse(target.value)

          const roundedNumber =
            countDecimalPlaces(number.toString()) > decimalPlaces
              ? number.toFixed(decimalPlaces)
              : number.toString()

          const newValue = Number(roundedNumber).toLocaleString(
            localeInfo.iso,
            {
              useGrouping: false,
            },
          )

          if (newValue === NaN.toString()) {
            return
          }

          target.value = newValue
          target.dispatchEvent(new Event('input', { bubbles: true }))
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        input.addEventListener('blur', parseNumber)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        el._parseNumber = parseNumber
      })
    },

    unmounted(el) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
      if (el && el.querySelector) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        const input = el.querySelector('input')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
        if (input && el._parseNumber && input.removeEventListener) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type el properly
          input.removeEventListener('blur', el._parseNumber)
        }
      }
    },
  })
})

function countDecimalPlaces(value: string) {
  const match = value.match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/)
  if (!match) {
    return 0
  }
  return Math.max(
    0,
    (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0),
  )
}
